import { ButtonLink } from "@teamscheme/button-link";
import { onDesktop } from "@teamscheme/theme";
import React from "react";
import styled from "styled-components";
import Card from "../../formatting/Card";
import { PageContent } from "../../formatting/Page";
import BankIdSigningLogo from "../../shared/logos/BankIdSigningLogo";
import { CreateSalesContractRequest } from "../create-contract/CreateContract";
import CreateContractForm from "../create-contract/CreateContractForm";
import { NafHr } from "./NafHr";
import SavedContracts, { SavedContract } from "./SavedContracts";

export interface CreateContractCardProps {
  bodyRef: React.MutableRefObject<HTMLDivElement | null>;
  createCardRef: React.MutableRefObject<HTMLDivElement | null>;
  hasSavedContracts: boolean;
  savedContracts: SavedContract[];
  showForm: boolean;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  setHasClickedShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  remove: (contract: SavedContract) => void;
  create: (request: CreateSalesContractRequest) => Promise<void>;
  lpnInputRef: React.MutableRefObject<HTMLInputElement | null>;
  className?: string;
}

export const SignedWithBankIdStyled = styled(BankIdSigningLogo)`
  max-width: 15em;
  height: auto;
`;

const CreateContractCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  width: 100%;
  align-items: center;
  position: relative;
  background: ${({ theme }) => theme.nafColor.signature.white};
`;

const CreateContractCardBody = styled.div`
  flex: 1 0 auto;

  ${onDesktop`
    display: flex;
    & > * {
      flex: 1;
    }
  `}
`;

export const BankIdWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.space40};
`;

export const ButtonLinkAndBankIDWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.space40};

  ${SignedWithBankIdStyled} {
    margin-top: ${({ theme }) => theme.spacing.space32};
  }

  ${onDesktop`
    flex-direction: row;
    align-items: center;
    ${SignedWithBankIdStyled} {
      margin-top: 0;
    }
  `}
`;

const StyledCard = styled(Card)`
  flex: 1;
  display: flex;
  flex-direction: column;
  flex: 0 0 30em;
  padding: 0;

  h1 {
    margin-top: 0;
  }

  ${NafHr} {
    margin-left: 0;
  }
`;

export default function CreateContractCard({
  bodyRef,
  createCardRef,
  hasSavedContracts,
  savedContracts,
  showForm,
  setShowForm,
  setHasClickedShowForm,
  remove,
  create,
  lpnInputRef,
  className,
}: CreateContractCardProps) {
  return (
    <CreateContractCardWrapper className={className}>
      <PageContent>
        <CreateContractCardBody ref={bodyRef}>
          <StyledCard ref={createCardRef}>
            <h1>NAF Kjøpekontrakt</h1>
            <p>
              For kjøp og salg av bruktbil og andre kjøretøy mellom
              privatpersoner. Fullført på et blunk og signert digitalt.
            </p>
            <NafHr />
            <div>
              {hasSavedContracts && !showForm ? (
                <SavedContracts
                  savedContracts={savedContracts}
                  showStartNew={hasSavedContracts}
                  onStartNew={() => {
                    setHasClickedShowForm(true);
                    setShowForm(true);
                  }}
                  onRemove={remove}
                />
              ) : (
                <>
                  <CreateContractForm
                    onSubmit={create}
                    inputRef={lpnInputRef}
                  />
                  {hasSavedContracts ? (
                    <ButtonLinkAndBankIDWrapper>
                      <ButtonLink
                        variant="secondary"
                        onClick={() => {
                          setHasClickedShowForm(true);
                          setShowForm(false);
                        }}
                      >
                        Se mine påbegynte
                      </ButtonLink>
                      <SignedWithBankIdStyled />
                    </ButtonLinkAndBankIDWrapper>
                  ) : (
                    <BankIdWrapper>
                      <SignedWithBankIdStyled />
                    </BankIdWrapper>
                  )}
                </>
              )}
            </div>
          </StyledCard>
        </CreateContractCardBody>
      </PageContent>
    </CreateContractCardWrapper>
  );
}
